import React, { useEffect, useMemo, useState } from 'react';
import { getNarrationBudget, NarrationBudget, shortenNarration } from '../../api/narration';
import { formatAxiosError } from '../../api/httpError';
import { ClipPrompt } from '../../api/structs/clip';
import { readNarrationMeasurement } from '../../api/structs/media';
import { useToast } from '../../hooks/useToast';
import { Button } from '../ui';
import {
  budgetSourceLabel,
  CALIBRATION_DEFAULT,
  CALIBRATION_MEASURED,
  clipDurationSeconds,
  countWords,
  FIT_OUT_OF_RANGE_HINT,
  fitSpeed,
  formatFitSeconds,
  isFitSpeedAllowed,
  narrationFitState,
  NarrationFitState,
  narratorTakeItem,
} from './narrationFit';

interface NarrationFitPanelProps {
  clip: ClipPrompt;
  provider: string;
  modelId: string;
  voice: string;
  speed: number;
  /** False when the model exposes no speed control — nothing to fit with, then. */
  canSetSpeed: boolean;
  /** Held back while the model's constraints are still loading. */
  isReady: boolean;
  /** A take is generating — neither action may start a second one. */
  isSubmitting: boolean;
  text: string;
  /** Approved rewrite — fills the modal's text box, and generates nothing. */
  onUseText: (text: string) => void;
  /** Regenerates the take at the fitting speed, replacing it as a normal generate does. */
  onFit: (speed: number) => void;
}

/** Settings changes come in bursts (model → voice → speed); only the last one is worth a call. */
const BUDGET_DEBOUNCE_MS = 300;

const TONE_CLASSES: Record<NarrationFitState, string> = {
  ok: 'text-zinc-400',
  long: 'text-red-300',
  short: 'text-amber-300',
};

const actionCls =
  'px-3 py-1.5 text-xs rounded-md border border-white/10 text-slate-400 transition-colors ' +
  'hover:text-white hover:border-white/30 disabled:opacity-40 disabled:hover:text-slate-400 ' +
  'disabled:hover:border-white/10 disabled:cursor-not-allowed';

/**
 * How the current take fits the clip, how many words the clip has room for, and
 * the two ways out of an overrun: regenerate faster, or have the text shortened.
 */
const NarrationFitPanel: React.FC<NarrationFitPanelProps> = ({
  clip,
  provider,
  modelId,
  voice,
  speed,
  canSetSpeed,
  isReady,
  isSubmitting,
  text,
  onUseText,
  onFit,
}) => {
  const toast = useToast();
  const [budget, setBudget] = useState<NarrationBudget | null>(null);
  const [shortened, setShortened] = useState<string | null>(null);
  const [isShortening, setIsShortening] = useState(false);

  const clipSeconds = useMemo(() => clipDurationSeconds(clip), [clip]);
  const measurement = useMemo(() => readNarrationMeasurement(narratorTakeItem(clip)), [clip]);
  // A clip without a single slide has no length to fit into yet — narration
  // written before the visuals exist. Nothing here has anything to say then.
  const hasClipLength = clipSeconds > 0;

  useEffect(() => {
    if (!isReady || !modelId || !hasClipLength) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      getNarrationBudget({ provider, model: modelId, voice, speed, seconds: clipSeconds })
        .then((next) => {
          if (!cancelled) setBudget(next);
        })
        .catch(() => {
          if (!cancelled) setBudget(null);
        });
    }, BUDGET_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isReady, hasClipLength, provider, modelId, voice, speed, clipSeconds]);

  // The approval box quotes word counts for one exact text and one exact
  // voice; edit either and the proposal is about something else.
  useEffect(() => {
    setShortened(null);
  }, [text, provider, modelId, voice, speed]);

  const fitState =
    measurement && hasClipLength
      ? narrationFitState(measurement.duration_seconds, clipSeconds)
      : null;
  const requiredSpeed = measurement
    ? fitSpeed(measurement.duration_seconds, clipSeconds, speed)
    : null;
  const canFit =
    !isSubmitting && canSetSpeed && fitState === 'long' && isFitSpeedAllowed(requiredSpeed);

  const handleShorten = async () => {
    if (!budget) return;
    setIsShortening(true);
    try {
      const proposal = await shortenNarration({
        text: text.trim(),
        words: budget.words,
        provider,
        model: modelId,
      });
      setShortened(proposal);
    } catch (error) {
      toast({ text: `Shorten failed: ${formatAxiosError(error)}`, level: 'error' });
    } finally {
      setIsShortening(false);
    }
  };

  if (!fitState && !budget) return null;

  return (
    <div className="space-y-2 border border-white/10 bg-white/[0.02] rounded-md px-3 py-2.5">
      {measurement && fitState && (
        <p className={`text-xs font-mono ${TONE_CLASSES[fitState]}`}>
          Narration {formatFitSeconds(measurement.duration_seconds)} s · clip{' '}
          {formatFitSeconds(clipSeconds)} s
        </p>
      )}

      {budget && (
        <p className="text-xs font-mono text-zinc-400">
          Budget ≈ {budget.words} words ({budgetSourceLabel(budget)})
        </p>
      )}

      <div className="flex gap-2">
        {fitState && (
          <button
            type="button"
            className={actionCls}
            disabled={!canFit}
            title={
              fitState === 'long' && !isFitSpeedAllowed(requiredSpeed)
                ? FIT_OUT_OF_RANGE_HINT
                : undefined
            }
            onClick={() => requiredSpeed !== null && onFit(requiredSpeed)}
          >
            Fit to clip
          </button>
        )}
        <button
          type="button"
          className={actionCls}
          disabled={!budget || isShortening || isSubmitting || text.trim() === ''}
          onClick={handleShorten}
        >
          {isShortening ? 'Shortening...' : 'Shorten text'}
        </button>
      </div>

      {budget && (
        <p className="text-[10px] text-zinc-600 font-mono">
          {budget.source === 'measured' ? CALIBRATION_MEASURED : CALIBRATION_DEFAULT}
        </p>
      )}

      {shortened !== null && (
        <div className="space-y-2 border border-indigo-400/30 bg-indigo-500/5 rounded-md p-2.5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-indigo-300 font-mono">
            Shortened · {countWords(text)} → {countWords(shortened)} words
          </p>
          <p className="text-sm text-white whitespace-pre-wrap">{shortened}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShortened(null)}>
              Discard
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                onUseText(shortened);
                setShortened(null);
              }}
            >
              Use this text
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NarrationFitPanel;
